import { ITableInfinite } from 'components/table/infinite/InfiniteTable.interfaces'
import styled from 'styled-components'
import UnitProductErrorTable from './errorListTable/UnitProductErrorTable'
import VatProducrErrorTable from './errorListTable/VatProductErrorTable'
import Info from 'components/Info'
import {
  ErrorProduct,
  ErrorProductType,
  ProductCreation,
  ProductCreationWithErrors,
} from '@Arti-zen/package-backoffice'
import CoefProductErrorTable from './errorListTable/CoefProductErrorTable'
import NetPriceProductErrorTable from './errorListTable/NetPriceProductError'
import { Theme } from 'theme/theme'
import { cleanText } from 'utils/schema.helper'

type TProps = {
  correctProduct: ({
    corrections,
    product,
  }: {
    corrections: Partial<ProductCreation>
    product: ProductCreationWithErrors
    index: number
  }) => void
  openForm: (product: ProductCreationWithErrors, index: number) => void
  hasId?: boolean
  replaceAllErrors: ({
    property,
    getValue,
    conditionInErr,
  }: {
    property: keyof ProductCreation
    getValue: (error: ErrorProduct) => any
    conditionInErr: (err: ErrorProduct) => boolean
  }) => void
  getNumberOfErrors: (condition: (err: ErrorProduct) => boolean) => number
  deleteProductsWithErrorsWithoutProp?: (prop: keyof ProductCreation) => void
}

export const definitionFields = ({
  correctProduct,
  openForm,
  hasId,
  replaceAllErrors,
  getNumberOfErrors,
  deleteProductsWithErrorsWithoutProp,
}: TProps): ITableInfinite<ProductCreationWithErrors>['definitionFields'] => {
  const findError = (errors: ErrorProduct[] | undefined, types: ErrorProductType[], property?: keyof ProductCreation) =>
    errors?.find(
      (error) =>
        types.includes(error.type) || (error.type === ErrorProductType.UNKNOWN_SYMBOL && property === error.property)
    )

  const numberOfErrors = {
    description: getNumberOfErrors(
      (err) => err.type === ErrorProductType.UNKNOWN_SYMBOL && err.property === 'description'
    ),
    noDescription: getNumberOfErrors((err) => err.type === ErrorProductType.NO_DESCRIPTION),
    allDescription: getNumberOfErrors(
      (err) => err.type === ErrorProductType.UNKNOWN_SYMBOL && err.property === 'allDescription'
    ),
    price: getNumberOfErrors(
      (err) => err.type === ErrorProductType.NAN_PRICE || err.type === ErrorProductType.NO_PRICE
    ),
    noPrice: getNumberOfErrors((err) => err.type === ErrorProductType.NO_PRICE),
    coef: getNumberOfErrors((err) => 'expectedCoef' in err),
    netPrice: getNumberOfErrors(
      (err) =>
        err.type === ErrorProductType.NAN_NETPRICE ||
        err.type === ErrorProductType.PRICE_MORE_THAN_NETPRICE ||
        'expectedNetPrice' in err
    ),
    vat: getNumberOfErrors((err) => 'vat' in err),
    unit: getNumberOfErrors((err) => 'unit' in err),
  }

  return [
    ...(hasId
      ? [
          {
            indexName: 'id' as const,
            label: 'ID produit',
            labelElement: (
              <LabelWrapper>
                <TitleLabel>ID produit</TitleLabel>
              </LabelWrapper>
            ),
            onClick: (product, index) => openForm(product, index),
          },
        ]
      : []),
    {
      indexName: 'description',
      label: 'Désignation* (description)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Désignation*</TitleLabel>
          {!!numberOfErrors.noDescription && (
            <LabelButton onClick={() => deleteProductsWithErrorsWithoutProp('description')}>
              Supprimer les produits sans désignation ({numberOfErrors.noDescription})
            </LabelButton>
          )}
          {!!numberOfErrors.description && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'description',
                  getValue: (err: {
                    type: ErrorProductType.UNKNOWN_SYMBOL
                    property: keyof ProductCreation
                    text: string
                  }) => cleanText(err.text),
                  conditionInErr: (err) =>
                    err.type === ErrorProductType.UNKNOWN_SYMBOL && err.property === 'description',
                })
              }
            >
              Nettoyer les descriptions en erreur ({numberOfErrors.description})
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) => openForm(product, index),
      format: ({ errors, description }) => {
        const error = findError(errors, [ErrorProductType.NO_DESCRIPTION], 'description')

        const getErrorMessage = () => {
          if (!error) return ''
          if ('property' in error)
            return `La désignation (description) "${error.text}" contient au moins un caractère invalide (emoji, caractère spécial...)`
          return "Le produit n'a pas de désignation (description)"
        }

        if (error) {
          return (
            <div style={{ position: 'relative' }}>
              <Error style={{ paddingRight: 25 }}>{'text' in error ? error.text : '_'}</Error>
              <Info text={getErrorMessage()} style={{ ...InfoRedStyle, top: 15, right: 0 }} absolute />
            </div>
          )
        }

        return <>{description}</>
      },
    },
    {
      indexName: 'allDescription',
      label: 'Description (allDescription)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Description</TitleLabel>
          {!!numberOfErrors.allDescription && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'allDescription',
                  conditionInErr: (err) =>
                    err.type === ErrorProductType.UNKNOWN_SYMBOL && err.property === 'allDescription',
                  getValue: () => null,
                })
              }
            >
              Supprimer toutes les descriptions en erreur ({numberOfErrors.allDescription}).
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) => openForm(product, index),
      format({ allDescription, errors }) {
        const error = findError(errors, [], 'allDescription')

        if (error) {
          return (
            <div style={{ position: 'relative' }}>
              <Error style={{ paddingRight: 25 }}>{allDescription}</Error>
              <Info
                text={
                  'La description (allDescription) contient au moins un caractère invalide (emoji, caractère spécial...)'
                }
                style={{ ...InfoRedStyle, top: 15, right: 0 }}
                absolute
              />
            </div>
          )
        }
        return <>{allDescription ? allDescription : '_'}</>
      },
    },
    {
      indexName: 'price',
      label: 'Prix HT facturé *(price)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Prix HT facturé*</TitleLabel>
          {!!numberOfErrors.noPrice && (
            <LabelButton onClick={() => deleteProductsWithErrorsWithoutProp('price')}>
              Supprimer les produits sans prix ({numberOfErrors.noPrice})
            </LabelButton>
          )}
          {!!numberOfErrors.price && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'price',
                  conditionInErr: (err) =>
                    err.type === ErrorProductType.NAN_PRICE || err.type === ErrorProductType.NO_PRICE,
                  getValue: () => 0,
                })
              }
            >
              Remplacer tous les prix en erreur ({numberOfErrors.price}) par 0€.
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) => openForm(product, index),
      format: ({ price, errors }) => {
        const error = findError(errors, [
          ErrorProductType.NO_PRICE,
          ErrorProductType.NAN_PRICE,
          ErrorProductType.PRICE_MORE_THAN_NETPRICE,
        ])

        const getErrorMessage = () => {
          if (!error) return ''
          switch (error.type) {
            case ErrorProductType.NO_PRICE:
              return 'Le produit ne contient pas de prix facturé HT (price)'
            case ErrorProductType.NAN_PRICE:
              return `Le prix facturé HT (price) renseigné "${error.price}" n'est pas un nombre`
            case ErrorProductType.PRICE_MORE_THAN_NETPRICE:
              return 'Le prix facturé HT (price) ne peut pas être moins élevé que le coût HT (netPrice)'
          }
        }

        if (error) {
          return (
            <Row>
              <Error>{price ? `${price} €` : '_'}</Error>
              <Info text={getErrorMessage()} style={InfoRedStyle} />
            </Row>
          )
        }
        return <>{price || price === 0 ? `${price} €` : '_'} </>
      },
    },

    {
      indexName: 'coef',
      label: 'Marge (coef)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Marge</TitleLabel>
          {!!numberOfErrors.coef && (
            <LabelButton
              onClick={() => {
                replaceAllErrors({
                  property: 'coef',
                  conditionInErr: (err) => 'expectedCoef' in err,
                  // @ts-expect-error
                  getValue: (err) => err.expectedCoef,
                })
              }}
            >
              Remplacer toutes les marges en erreur ({numberOfErrors.coef}) par les recommendations.
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) => openForm(product, index),
      format: (product, _, index) => {
        const error = findError(product.errors, [ErrorProductType.NAN_COEF, ErrorProductType.COEF_NETPRICE_INCORRECT])

        if (error && 'expectedCoef' in error) {
          return (
            <CoefProductErrorTable
              givenCoef={product.coef}
              expectedCoef={error.expectedCoef}
              correctProduct={(corrections: Partial<ProductCreation>) =>
                correctProduct({ corrections, product, index })
              }
              errorMessage={`Le calcul entre prix (netPrice), marge(coef) et coût HT(price) est faux.\nMarge (coef) proposée pour ajuster le calcul : ${error.expectedCoef}%`}
            />
          )
        }
        if (error && 'coef' in error) {
          return (
            <Row>
              <Error>_</Error>
              <Info text={`La marge (coef) renseignée "${error.coef}" n'est pas un nombre.`} style={InfoRedStyle} />
            </Row>
          )
        }

        return <>{product.coef || product.coef === 0 ? `${product.coef} %` : '_'}</>
      },
    },
    {
      indexName: 'netPrice',
      label: 'Coût HT (netPrice)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Coût HT</TitleLabel>
          {!!numberOfErrors.netPrice && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'netPrice',
                  conditionInErr: (err) =>
                    err.type === ErrorProductType.NAN_NETPRICE ||
                    err.type === ErrorProductType.PRICE_MORE_THAN_NETPRICE ||
                    'expectedNetPrice' in err,
                  getValue: (error) => ('expectedNetPrice' in error ? error.expectedNetPrice : null),
                })
              }
            >
              Remplacer tous les coûts HT en erreur ({numberOfErrors.netPrice}) par les recommendations.
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) => openForm(product, index),
      format(product, _, index) {
        const error = findError(product.errors, [
          ErrorProductType.NAN_NETPRICE,
          ErrorProductType.COEF_NETPRICE_INCORRECT,
          ErrorProductType.PRICE_MORE_THAN_NETPRICE,
        ])

        const getErrorMessage = () => {
          if (!error) return ''
          switch (error.type) {
            case ErrorProductType.NAN_NETPRICE:
              return `Le coût HT (netPrice) renseigné "${error.netPrice}" n'est pas un nombre.`
            case ErrorProductType.PRICE_MORE_THAN_NETPRICE:
              return 'Le prix facturé HT (price) ne peut pas être moins élevé que le coût HT (netPrice)'
          }
        }

        if (error && 'expectedNetPrice' in error) {
          return (
            <NetPriceProductErrorTable
              givenNetPrice={product.netPrice}
              expectedNetPrice={error.expectedNetPrice}
              correctProduct={(corrections: Partial<ProductCreation>) =>
                correctProduct({ corrections, product, index })
              }
              errorMessage={`Le calcul entre prix HT facturé (price), marge(coef) et coût HT (netPrice) est faux.\nCoût HT (netPrice) proposé pour ajuster le calcul : ${error.expectedNetPrice}€`}
            />
          )
        }

        if (error && 'netPrice' in error) {
          return (
            <Row>
              <Error>{product?.netPrice ? `${product.netPrice} €` : '_'}</Error>
              <Info text={getErrorMessage()} style={InfoRedStyle} />
            </Row>
          )
        }
        return <>{product?.netPrice ? `${product.netPrice} €` : '_'}</>
      },
    },

    {
      indexName: 'vat',
      label: 'TVA (vat)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>TVA</TitleLabel>
          {!!numberOfErrors.vat && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'vat',
                  conditionInErr: (err) => 'vat' in err,
                  getValue: () => null,
                })
              }
            >
              Supprimer toutes les TVA en erreur ({numberOfErrors.vat}).
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) =>
        !!findError(product.errors, [ErrorProductType.UNKNOWN_VAT]) ? {} : openForm(product, index),
      format: (product, isHover, index) => {
        const error = findError(product.errors, [ErrorProductType.UNKNOWN_VAT])

        if (error?.type === ErrorProductType.UNKNOWN_VAT) {
          return (
            <VatProducrErrorTable
              currentVat={error.vat}
              errorMessage={`Le taux de TVA renseigné "${error.vat}%" n'existe pas.`}
              correctProduct={(corrections: Partial<ProductCreation>) =>
                correctProduct({ corrections, product, index })
              }
            />
          )
        }
        return <>{product?.vat || product?.vat === 0 ? `${product.vat}%` : '_'}</>
      },
    },
    {
      indexName: 'unit',
      label: 'Unité (unit)',
      labelElement: (
        <LabelWrapper>
          <TitleLabel>Unité</TitleLabel>
          {!!numberOfErrors.unit && (
            <LabelButton
              onClick={() =>
                replaceAllErrors({
                  property: 'unit',
                  conditionInErr: (err) => 'unit' in err,
                  getValue: () => null,
                })
              }
            >
              Supprimer toutes les unités en erreur ({numberOfErrors.unit}).
            </LabelButton>
          )}
        </LabelWrapper>
      ),
      onClick: (product, index) =>
        findError(product.errors, [ErrorProductType.UNKNOWN_UNIT]) ? {} : openForm(product, index),
      format: (product, _, index) => {
        const error = findError(product.errors, [ErrorProductType.UNKNOWN_UNIT])

        if (error?.type === ErrorProductType.UNKNOWN_UNIT) {
          return (
            <UnitProductErrorTable
              currentUnit={error.unit}
              errorMessage={`L'unité renseignée "${error.unit}" n'existe pas chez Artizen.`}
              correctProduct={(corrections: Partial<ProductCreation>) =>
                correctProduct({ corrections, product, index })
              }
            />
          )
        }

        return <>{product?.unit ? product.unit : '_'}</>
      },
    },
  ]
}

export const Error = styled.div`
  color: red;
`

export const ValidButton = styled.div`
  background-color: #1edb97;
  padding: 0 8px;
  height: 40px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  color: white;
  font-family: Roboto;

  &:hover {
    cursor: pointer;
  }
`
export const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  flex: 1;
`
export const InfoRedStyle = { backgroundColor: 'red', color: 'white', fontWeight: 'bold' }

const LabelWrapper = styled.div`
  padding: 10px 5px 5px 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-self: flex-start;
`

const TitleLabel = styled.h2`
  color: white;
  font-size: 14px;
  font-family: Roboto;
  margin: 0;
  text-transform: none;
`

const LabelButton = styled.div`
  background-color: white;
  color: ${Theme.colors.blueArtizen};
  padding: 5px;
  border-radius: 3px;
  text-transform: none;
  &:hover {
    background-color: ${Theme.colors.greenArtizen};
    color: white;
    cursor: pointer;
  }
`
